import { FC, useMemo } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { ReactComponent as ReloadIcon } from 'src/assets/svg/Reload.svg'
import { ReactComponent as ExportIcon } from 'src/assets/svg/blue-file-download.svg'
import { BackNavigationButton } from 'src/components/BackNavigationButton'
import { ActivityFrequencySection } from 'src/charts/ActivityFrequencySection'
import { CommentsChartSection } from 'src/charts/CommentsChartSection'
import { CommentsAnalysisSection } from 'src/charts/ContentAnalysisV2/CommentsAnalysis'
import { ContentAnalysisOverview } from 'src/charts/ContentAnalysisV2/Overview'
import { ContentTopicsSection } from 'src/charts/ContentAnalysisV2/TopicsSection'
import { ContentPerformanceSection } from 'src/charts/ContentPerformanceSection'
import { EngagementInsightsSection } from 'src/charts/EngagementInsightsSection'
import { IndividualPostsSection } from 'src/charts/IndividualPostsSection'
import { IndividualPostsResponse } from 'src/services/__generated__/api'
import {
  SmartInsightsAIRecommendations,
  CONTENT_ANALYSIS_FILTER_TABS,
} from 'src/charts/SmartInsightsAIRecommendations'
import { SummarySection } from 'src/charts/SummarySection'
import { EmptyState } from 'src/components/EmptyState'
import { CONTENT_ANALYSIS_ROUTE } from 'src/constants/routes'
import { platformDisplayLabel } from 'src/constants/platformLabels'
import { SOCIAL_ICONS } from 'src/constants/socialIcons'
import { useContentAnalysisV2Range } from 'src/hooks/useContentAnalysisV2Range'
import { useCustomLink } from 'src/hooks/useCustomLink'
import { CATEGORY_CONFIG } from 'src/pages/ContentAnalysis/ContentAnalysisCard'
import { useGetContentAnalysisActivityFrequencyQuery } from 'src/reactQuery/useGetContentAnalysisActivityFrequencyQuery'
import { useGetContentAnalysisCommentsQuery } from 'src/reactQuery/useGetContentAnalysisCommentsQuery'
import { useGetContentAnalysisContentPerformanceQuery } from 'src/reactQuery/useGetContentAnalysisContentPerformanceQuery'
import { useGetContentAnalysisEngagementInsightsQuery } from 'src/reactQuery/useGetContentAnalysisEngagementInsightsQuery'
import { useGetContentAnalysisIndividualPostsQuery } from 'src/reactQuery/useGetContentAnalysisIndividualPostsQuery'
import { useGetContentAnalysisQuery } from 'src/reactQuery/useGetContentAnalysisQuery'
import { useGetContentAnalysisSmartInsightsQuery } from 'src/reactQuery/useGetContentAnalysisSmartInsightsQuery'
import { useExportContentAnalysisPostsMutation } from 'src/reactQuery/useExportContentAnalysisPostsMutation'
import {
  ContentAnalysisPageUrl,
  ContentAnalysisPlatform,
  ContentAnalysisType,
} from 'src/services/contentAnalysisApi/interfaces/contentAnalysis.interface'
import {
  ActivityFrequencyMockData,
  activityFrequencyEngagementMockData,
  activityFrequencyPostsMockData,
} from './mockData/activityFrequencyMockData'
import {
  CommentsChartMockData,
  commentsChartMockData,
} from './mockData/commentsChartMockData'
import {
  ContentPerformanceMockData,
  contentPerformanceMockData,
} from './mockData/contentPerformanceMockData'
import {
  EngagementInsightsMockData,
  engagementInsightsMockData,
} from './mockData/engagementInsightsMockData'
import { individualPostsMockData } from './mockData/individualPostsMockData'
import { smartInsightsMockData } from './mockData/smartInsightsMockData'
import styles from './contentAnalysisDetail.module.scss'

// TEMPORARY MOCK DATA FOR DEVELOPMENT
const MOCK_DATA_ENABLED = false

const EMPTY_FREQUENCY_DATA: ActivityFrequencyMockData = {
  allPlatform: {
    data: [],
    visibleHours: [
      '00:00',
      '03:00',
      '06:00',
      '09:00',
      '12:00',
      '15:00',
      '18:00',
      '21:00',
    ],
    xAxis: [
      'Monday',
      'Tuesday',
      'Wednesday',
      'Thursday',
      'Friday',
      'Saturday',
      'Sunday',
    ],
    yAxis: Array.from(
      { length: 24 },
      (_, i) => `${String(i).padStart(2, '0')}:00`
    ),
  },
}

// Empty shells so each widget can render its own structural skeleton while its
// query is in flight, instead of being swapped out for a flat placeholder block
// that matches none of their real heights.
const EMPTY_CONTENT_PERFORMANCE_DATA: ContentPerformanceMockData = {
  platforms: [],
}

const EMPTY_ENGAGEMENT_INSIGHTS_DATA: EngagementInsightsMockData = {
  emojis: {
    captions: {},
    comments: {},
  } as EngagementInsightsMockData['emojis'],
  phrases: {
    captions: {},
    comments: {},
  } as EngagementInsightsMockData['phrases'],
  reactions: {} as EngagementInsightsMockData['reactions'],
}

const EMPTY_INDIVIDUAL_POSTS_DATA: IndividualPostsResponse['data'] = {
  posts: [],
}

const EMPTY_COMMENTS_DATA: CommentsChartMockData = {
  comments: [],
  sentimentBreakdown: {
    negative: 0,
    negativePercentage: 0,
    neutral: 0,
    neutralPercentage: 0,
    positive: 0,
    positivePercentage: 0,
  },
  totalComments: 0,
}

const mockAnalysisData: ContentAnalysisType = {
  _id: 'mock-id',
  createdAt: '2024-01-05T00:00:00.000Z',
  negatives: 614,
  negativesPerc: 15.2,
  neutrals: 1021,
  neutralsPerc: 25.2,
  positives: 2417,
  positivesPerc: 59.6,
  quickStats: {
    comments: 4052,
    reactions: 44415,
    shares: 3924,
  },
  sources: [
    { pageLink: 'https://www.facebook.com/example', platform: 'facebook' },
    { pageLink: 'https://www.instagram.com/example', platform: 'instagram' },
  ],
  status: 'active',
  title: 'Test Analysis',
  topPhrases: [
    { count: 4847, keyword: 'Amazing' },
    { count: 3187, keyword: 'Adventure' },
    { count: 2843, keyword: 'Beautiful' },
  ],
  totalPosts: 47,
  updatedAt: '2024-01-14T00:00:00.000Z',
  urlsCount: 3,
  user: 'demo-user',
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getLink = (u: any): string | null =>
  u.link || u.pageLink || u.postLink || null

const extractSources = (
  data: ContentAnalysisType | undefined
): ContentAnalysisPageUrl[] => {
  if (!data) return []

  // Build a link lookup from recentPosts/captionSearch URLs
  const linkMap = new Map<string, string | null>()
  data.recentPosts?.urls?.forEach((u) => {
    const link = getLink(u)
    if (u.platform && link) linkMap.set(u.platform, link)
  })
  data.captionSearch?.urls?.forEach((u) => {
    const link = getLink(u)
    if (u.platform && link && !linkMap.has(u.platform)) {
      linkMap.set(u.platform, link)
    }
  })

  if (data.sources && data.sources.length > 0) {
    // Handle both old format (string[]) and new format (object[])
    return data.sources
      .filter((s) =>
        typeof s === 'string' ? s : typeof s.platform === 'string' && s.platform
      )
      .map((s) => {
        if (typeof s === 'string') {
          return {
            pageLink: linkMap.get(s) || null,
            platform: s as ContentAnalysisPlatform,
          }
        }
        const sourceLink = getLink(s)
        // Enrich with link from URLs if missing
        if (!sourceLink && linkMap.has(s.platform)) {
          return { ...s, pageLink: linkMap.get(s.platform) || null }
        }
        return { ...s, pageLink: sourceLink }
      })
  }

  return Array.from(linkMap.entries()).map(([platform, pageLink]) => ({
    pageLink,
    platform: platform as ContentAnalysisPlatform,
  }))
}

export const ContentAnalysisDetail: FC = () => {
  const { analysisId } = useParams<{ analysisId: string }>()
  const [searchParams] = useSearchParams()
  const fromDate = searchParams.get('from') || undefined
  const toDate = searchParams.get('to') || undefined
  const backURL = useCustomLink(CONTENT_ANALYSIS_ROUTE)
  const v2Range = useContentAnalysisV2Range()
  const analysisIds = useMemo(
    () => (analysisId ? [analysisId] : undefined),
    [analysisId]
  )

  const { data: contentAnalysis, isLoading } = useGetContentAnalysisQuery({
    id: analysisId || '',
  })

  const { data: activityFrequency, isFetching: activityFrequencyFetching } =
    useGetContentAnalysisActivityFrequencyQuery({
      fromDate,
      id: analysisId || '',
      toDate,
    })
  // isLoading, not isFetching: the date range is part of the query key, so a
  // new range still shows the skeleton, but the invalidation refetch after a
  // sentiment re-label has cached data and must update in place — swapping the
  // whole section to a skeleton made the pie chart flash and remount.
  const { data: comments, isLoading: commentsLoading } =
    useGetContentAnalysisCommentsQuery({
      fromDate,
      id: analysisId || '',
      toDate,
    })
  const { data: contentPerformance, isFetching: contentPerformanceFetching } =
    useGetContentAnalysisContentPerformanceQuery({
      fromDate,
      id: analysisId || '',
      toDate,
    })
  const { data: engagementInsights, isFetching: engagementInsightsFetching } =
    useGetContentAnalysisEngagementInsightsQuery({
      fromDate,
      id: analysisId || '',
      toDate,
    })
  // isLoading for the same reason as the comments query above: the sentiment
  // re-label mutation invalidates this query too, and an isFetching gate made
  // the posts strip unmount into skeletons on every re-label.
  const { data: individualPosts, isLoading: individualPostsLoading } =
    useGetContentAnalysisIndividualPostsQuery({
      fromDate,
      id: analysisId || '',
      toDate,
    })
  const {
    data: smartInsights,
    isFetching: smartInsightsFetching,
    isLoading: smartInsightsLoading,
  } = useGetContentAnalysisSmartInsightsQuery({
    fromDate,
    id: analysisId || '',
    toDate,
  })

  const { mutate: exportPosts, isPending: isExporting } =
    useExportContentAnalysisPostsMutation()

  const analysisData = MOCK_DATA_ENABLED
    ? mockAnalysisData
    : contentAnalysis?.data
  const sources = useMemo(() => extractSources(analysisData), [analysisData])
  const platformNames = useMemo(() => sources.map((s) => s.platform), [sources])

  // Use mock data when enabled
  const actualContentPerformance = MOCK_DATA_ENABLED
    ? { data: contentPerformanceMockData }
    : contentPerformance
  const actualActivityFrequency = MOCK_DATA_ENABLED
    ? {
        data: {
          engagement: activityFrequencyEngagementMockData,
          posts: activityFrequencyPostsMockData,
        },
      }
    : activityFrequency
  const actualEngagementInsights = MOCK_DATA_ENABLED
    ? { data: engagementInsightsMockData }
    : engagementInsights
  const actualIndividualPosts = MOCK_DATA_ENABLED
    ? { data: individualPostsMockData }
    : individualPosts
  const actualComments = MOCK_DATA_ENABLED
    ? { data: commentsChartMockData }
    : comments
  const actualSmartInsights = MOCK_DATA_ENABLED
    ? { data: smartInsightsMockData }
    : smartInsights

  const postsCount = actualIndividualPosts?.data?.totalCount ?? 0
  const category = analysisData?.categories?.[0]
  const categoryConfig = category ? CATEGORY_CONFIG[category] : null
  const CategoryIcon = categoryConfig?.icon

  const handleDownloadLinks = () => {
    if (!analysisId) return

    const slug =
      (analysisData?.title || 'content-analysis')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 60) || 'content-analysis'

    exportPosts(
      { fromDate, id: analysisId, toDate },
      {
        onError: () => {
          toast.error('Failed to download links. Please try again.')
        },
        onSuccess: (blob) => {
          const url = window.URL.createObjectURL(blob)
          const link = document.createElement('a')
          link.href = url
          link.download = `${slug}-posts-${
            new Date().toISOString().split('T')[0]
          }.csv`
          document.body.appendChild(link)
          link.click()
          link.remove()
          window.URL.revokeObjectURL(url)
          toast.success('Links downloaded successfully!')
        },
      }
    )
  }

  const hasEverBeenProcessed = MOCK_DATA_ENABLED
    ? true
    : !!analysisData?.updatedAt

  if (isLoading) {
    return (
      <div className={styles.contentAnalysisDetail}>
        <BackNavigationButton title="Loading..." url={backURL} />
        <div className={styles.loadingSection}>
          <div className={styles.loadingContent}>
            <div className={styles.loadingTitle}>Loading...</div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.contentAnalysisDetail}>
      <div className={styles.backButtonWrapper}>
        <BackNavigationButton
          title={analysisData?.title || 'Content Analysis'}
          url={backURL}
        />
        <div className={styles.headerActions}>
          {categoryConfig && CategoryIcon && (
            <span
              className={styles.categoryPill}
              style={{
                background: categoryConfig.bg,
                color: categoryConfig.color,
              }}
            >
              <CategoryIcon className={styles.categoryPillIcon} />
              {category}
            </span>
          )}
          {hasEverBeenProcessed && (
            <button
              className={styles.downloadButton}
              disabled={isExporting || postsCount === 0}
              onClick={handleDownloadLinks}
              type="button"
            >
              <ExportIcon />
              <span>{isExporting ? 'Preparing…' : 'Download links'}</span>
            </button>
          )}
        </div>
      </div>
      {sources.length > 0 && (
        <div className={styles.sourcesSection}>
          <div className={styles.sourcesList}>
            {sources.map((source) =>
              source.pageLink ? (
                <a
                  className={styles.sourceLink}
                  href={source.pageLink}
                  key={source.platform}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  <span className={styles.sourceIcon}>
                    {SOCIAL_ICONS[source.platform.toLowerCase()]}
                  </span>
                  <span className={styles.sourceName}>
                    {platformDisplayLabel(
                      source.platform,
                      source.platform.charAt(0).toUpperCase() +
                        source.platform.slice(1)
                    )}
                  </span>
                  <svg
                    className={styles.externalIcon}
                    fill="none"
                    height="16"
                    viewBox="0 0 24 24"
                    width="16"
                  >
                    <path
                      d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6M15 3h6v6M10 14L21 3"
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                    />
                  </svg>
                </a>
              ) : (
                <div className={styles.sourceLink} key={source.platform}>
                  <span className={styles.sourceIcon}>
                    {SOCIAL_ICONS[source.platform.toLowerCase()]}
                  </span>
                  <span className={styles.sourceName}>
                    {platformDisplayLabel(
                      source.platform,
                      source.platform.charAt(0).toUpperCase() +
                        source.platform.slice(1)
                    )}
                  </span>
                </div>
              )
            )}
          </div>
        </div>
      )}

      {hasEverBeenProcessed ? (
        <>
          <div className={styles.chartSection}>
            <ContentAnalysisOverview
              analysisIds={analysisIds}
              range={v2Range}
              variant="detail"
            />
          </div>

          <div className={styles.chartSection}>
            <ActivityFrequencySection
              engagementData={
                actualActivityFrequency?.data?.engagement ||
                EMPTY_FREQUENCY_DATA
              }
              isLoading={activityFrequencyFetching}
              platforms={platformNames}
              postsData={
                actualActivityFrequency?.data?.posts || EMPTY_FREQUENCY_DATA
              }
            />
          </div>

          <div className={styles.chartSection}>
            {/* Loading is a branch of its own, ahead of the empty state: while
                the query is in flight the widget renders its own skeleton at its
                real size rather than claiming there is no data. */}
            {contentPerformanceFetching ? (
              <ContentPerformanceSection
                data={EMPTY_CONTENT_PERFORMANCE_DATA}
                isLoading
                platforms={platformNames}
              />
            ) : actualContentPerformance?.data ? (
              <ContentPerformanceSection
                data={actualContentPerformance.data}
                platforms={platformNames}
              />
            ) : (
              <EmptyState
                description="Try selecting a different date range"
                title="No performance data for this period"
              />
            )}
          </div>

          <div className={styles.chartSection}>
            {engagementInsightsFetching ? (
              <EngagementInsightsSection
                data={EMPTY_ENGAGEMENT_INSIGHTS_DATA}
                isLoading
                platforms={platformNames}
              />
            ) : actualEngagementInsights?.data ? (
              <EngagementInsightsSection
                data={actualEngagementInsights.data}
                platforms={platformNames}
              />
            ) : (
              <EmptyState
                description="Try selecting a different date range"
                title="No engagement data for this period"
              />
            )}
          </div>

          <div className={styles.chartSection}>
            {individualPostsLoading ? (
              <IndividualPostsSection
                data={EMPTY_INDIVIDUAL_POSTS_DATA}
                isLoading
              />
            ) : actualIndividualPosts?.data ? (
              <IndividualPostsSection data={actualIndividualPosts.data} />
            ) : (
              <EmptyState
                description="Try selecting a different date range"
                title="No posts data for this period"
              />
            )}
          </div>

          <div className={styles.chartSection}>
            {commentsLoading ? (
              <CommentsChartSection
                analysisId={analysisId || ''}
                data={EMPTY_COMMENTS_DATA}
                dateRangeKey={`${fromDate || ''}|${toDate || ''}`}
                isLoading
                platforms={platformNames}
              />
            ) : actualComments?.data ? (
              <CommentsChartSection
                analysisId={analysisId || ''}
                data={actualComments.data}
                dateRangeKey={`${fromDate || ''}|${toDate || ''}`}
                platforms={platformNames}
              />
            ) : (
              <EmptyState
                description="Try selecting a different date range"
                title="No comments data for this period"
              />
            )}
          </div>

          <div className={styles.chartSection}>
            <CommentsAnalysisSection
              analysisIds={analysisIds}
              platforms={platformNames}
              range={v2Range}
            />
          </div>

          <div className={styles.chartSection}>
            <ContentTopicsSection analysisIds={analysisIds} range={v2Range} />
          </div>

          <div className={styles.chartSection}>
            <SmartInsightsAIRecommendations
              // A Competitor Analysis never carries competitor_moves (the
              // backend rewrites it), so hide that chip here exactly as
              // ThemeDetail does for Competitor Analysis campaigns.
              campaignCategories={analysisData?.categories}
              data={actualSmartInsights?.data}
              filterTabs={CONTENT_ANALYSIS_FILTER_TABS}
              homePageVariant
              isLoading={smartInsightsLoading || smartInsightsFetching}
              modalMetricsVariant="content"
            />
          </div>

          <div className={styles.chartSection}>
            <SummarySection analysisId={analysisId || ''} />
          </div>
        </>
      ) : (
        <div className={styles.loadingSection}>
          <div className={styles.loadingContent}>
            <ReloadIcon className={styles.loadingIcon} />
            <div className={styles.loadingTitle}>
              We&apos;re Analyzing Your Data
            </div>
            <p className={styles.loadingText}>
              This may take a few moments. We&apos;re scanning posts, keywords,
              and engagement data.
            </p>
            <p className={styles.loadingText}>
              Data usually appears in a few hours.
            </p>
          </div>
        </div>
      )}
    </div>
  )
}
